"use client";

// @ubm/product-ui/blocks/sidebar-rail — HOUSE IMPLEMENTATION from the public UIArc specification
// (https://uiarc.dev/components/blocks/sidebar/markdown, variante App rail) ; alias public du canon Sidebar ; original Pro
// implementation not received. Composition : Tooltip, Avatar, AnimatedCounter, Drawer canons (../*).
// Adaptations maison déclarées : sections, éléments, fil d'Ariane, personne et contenu sont INJECTÉS ; la navigation est un
// port (`onNavigate`) qui peut refuser (false ou rejet) : l'élément actif ne bouge qu'après acceptation, et l'élément actif
// contrôlé se re-synchronise (routeur hôte). Un SEUL arbre de navigation, rendu soit dans le rail, soit — sous 640 px de
// largeur de CONTENEUR — dans le Drawer canon (≤ 288 px, glisser à gauche pour fermer, Échap, retour du focus, fermé dès que
// le conteneur s'élargit). Ce bloc ne remplace pas la navigation de l'Admin : c'est un cadre d'application à composer.
import { forwardRef, useEffect, useId, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { AnimatePresence, motion, useReducedMotionConfig as useReducedMotion, type HTMLMotionProps } from "motion/react";
import { ChevronRight, Menu, PanelLeft } from "lucide-react";
import { AnimatedCounter } from "../animated-counter";
import { Avatar } from "../avatar";
import { Drawer, DrawerContent } from "../drawer";
import { Tooltip } from "../tooltip";
import { motionTokens } from "../../lib/motion-tokens";
import { LiveStatus, shortcutAllowed, useContainerWidth, useLeavingProps } from "./support";
import styles from "./sidebar-rail.module.css";

export interface RailItem {
  id: string;
  label: string;
  icon?: ReactNode;
  /** A count shown at the end of the row (rolls when it changes). */
  badge?: number;
  /** One nested level: the row becomes a group that expands. */
  children?: RailItem[];
}
export interface RailSection { id: string; label?: string; items: RailItem[] }
export interface RailPerson { name: string; email?: string; avatar?: string }
export interface RailContentContext {
  active: RailItem | undefined;
  /** True when the content area is narrower than 420px: drop secondary metadata. */
  compact: boolean;
}
export type RailNavigateResult = boolean | void;
export interface SidebarRailStrings {
  nav?: string;
  menu?: string;
  toggle?: string;
  opened?: (label: string) => string;
  refused?: (label: string) => string;
  breadcrumb?: string;
}
export interface SidebarRailProps {
  /** App or workspace name at the top of the sidebar. */
  brand?: ReactNode;
  sections: RailSection[];
  activeId?: string;
  defaultActiveId?: string;
  /** Route to an item. Return false, or reject, to refuse: the active item stays where it was. */
  onNavigate?: (item: RailItem) => RailNavigateResult | Promise<RailNavigateResult>;
  user?: RailPerson;
  collapsed?: boolean;
  defaultCollapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  /** Page content. A function receives the active item and the compact flag. */
  children?: ReactNode | ((context: RailContentContext) => ReactNode);
  /** Frame height; the content scrolls inside. Defaults to 640. */
  height?: number | string;
  strings?: SidebarRailStrings;
  className?: string;
  style?: CSSProperties;
}

const STRINGS: Required<SidebarRailStrings> = {
  nav: "Main navigation",
  menu: "Open navigation",
  toggle: "Toggle sidebar",
  opened: label => `${label} opened.`,
  refused: label => `${label} couldn't be opened.`,
  breadcrumb: "Breadcrumb",
};
/** A group's child list; while it leaves (collapse) it is inert and hidden: no focus, no Enter, no click navigates. */
const ChildList = forwardRef<HTMLUListElement, HTMLMotionProps<"ul">>(function ChildList(props, ref) {
  return <motion.ul ref={ref} {...props} {...useLeavingProps()} />;
});

const RAIL = 64, FULL = 248, NARROW = 640, COMPACT = 420;

function locate(sections: RailSection[], id: string | undefined) {
  if (!id) return undefined;
  for (const section of sections) for (const item of section.items) {
    if (item.id === id) return { section, parent: undefined as RailItem | undefined, item };
    const child = item.children?.find(c => c.id === id);
    if (child) return { section, parent: item, item: child };
  }
  return undefined;
}

export function SidebarRail({ brand, sections, activeId: activeProp, defaultActiveId, onNavigate, user, collapsed: collapsedProp, defaultCollapsed = false, onCollapsedChange, children, height = 640, strings, className, style }: SidebarRailProps) {
  const t = { ...STRINGS, ...strings };
  const reduced = !!useReducedMotion();
  const uid = useId();
  const root = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const width = useContainerWidth(root, 1024);
  const contentWidth = useContainerWidth(content, 1024);
  const narrow = width > 0 && width < NARROW;
  const compact = contentWidth > 0 && contentWidth < COMPACT;
  const [ownActive, setOwnActive] = useState(defaultActiveId ?? sections[0]?.items[0]?.id);
  const activeId = activeProp ?? ownActive;
  const found = locate(sections, activeId);
  const [ownCollapsed, setOwnCollapsed] = useState(defaultCollapsed);
  const collapsed = !narrow && (collapsedProp ?? ownCollapsed);
  const [open, setOpen] = useState<Set<string>>(() => new Set(found?.parent ? [found.parent.id] : []));
  const [sheet, setSheet] = useState(false);
  const [pending, setPending] = useState<string | null>(null);
  const [status, setStatus] = useState("");
  const rows = useRef(new Map<string, HTMLButtonElement>());

  // An external route change opens the group that holds the new active item.
  useEffect(() => {
    const parent = found?.parent?.id;
    if (parent) setOpen(cur => (cur.has(parent) ? cur : new Set(cur).add(parent)));
  }, [found?.parent?.id]);
  // The sheet only exists on narrow frames: widening the container closes it.
  useEffect(() => { if (!narrow) setSheet(false); }, [narrow]);

  const setCollapsed = (next: boolean) => { if (narrow) return; if (collapsedProp === undefined) setOwnCollapsed(next); onCollapsedChange?.(next); };
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "[" || !shortcutAllowed(event)) return;
      const target = event.target as Node | null;
      if (target && target !== document.body && !root.current?.contains(target)) return;
      event.preventDefault();
      setCollapsed(!collapsed);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  const navigate = async (item: RailItem) => {
    if (pending || item.id === activeId) { setSheet(false); return; }
    setPending(item.id);
    let accepted: boolean;
    try { accepted = (await onNavigate?.(item)) !== false; }
    catch { accepted = false; }
    setPending(null);
    if (!accepted) { setStatus(t.refused(item.label)); return; }
    if (activeProp === undefined) setOwnActive(item.id);
    setStatus(t.opened(item.label));
    setSheet(false);
  };
  const toggleGroup = (id: string) => setOpen(cur => { const next = new Set(cur); if (next.has(id)) next.delete(id); else next.add(id); return next; });

  const crumbs = useMemo(() => [found?.section.label, found?.parent?.label, found?.item.label].filter((x): x is string => !!x), [found]);
  const spring = reduced ? { duration: 0 } : motionTokens.spring.smooth;
  const showLabels = !collapsed;

  const row = (item: RailItem, depth: number) => {
    const isGroup = !!item.children?.length;
    const expanded = isGroup && (open.has(item.id) || collapsed);
    const current = item.id === activeId;
    const holdsCurrent = isGroup && item.children!.some(c => c.id === activeId);
    const highlighted = current || (holdsCurrent && (collapsed || !expanded));
    const button = <button ref={el => { if (el) rows.current.set(item.id, el); else rows.current.delete(item.id); }} type="button" className={styles.row} data-depth={depth}
      aria-current={current ? "page" : undefined} aria-expanded={isGroup && showLabels ? expanded : undefined} aria-busy={pending === item.id || undefined}
      data-active={highlighted || undefined} data-holds={holdsCurrent || undefined} aria-label={!showLabels ? `${item.label}${item.badge ? `, ${item.badge}` : ""}` : undefined}
      onClick={event => {
        // A row inside a leaving (inert) list never acts, even if an event reaches it programmatically.
        if (event.currentTarget.closest("[inert]")) return;
        if (isGroup && showLabels) toggleGroup(item.id); else void navigate(isGroup ? item.children![0]! : item);
      }}>
      {highlighted && <motion.span aria-hidden="true" className={styles.activeIndicator} layoutId={`${uid}-active`} initial={false} transition={reduced ? { duration: 0 } : motionTokens.spring.snappy} />}
      {item.icon && <span className={styles.icon} aria-hidden="true">{item.icon}</span>}
      {showLabels && <span className={styles.label}>{item.label}</span>}
      {showLabels && typeof item.badge === "number" && item.badge > 0 && <span className={styles.badge}><AnimatedCounter value={item.badge} /></span>}
      {showLabels && isGroup && <motion.span className={styles.chevron} aria-hidden="true" initial={false} animate={{ rotate: expanded ? 90 : 0 }} transition={spring}><ChevronRight size={14} /></motion.span>}
    </button>;
    return <li key={item.id}>
      {!showLabels ? <Tooltip content={item.label} side="right" disableHoverableContent>{button}</Tooltip> : button}
      {isGroup && showLabels && <AnimatePresence initial={false}>
        {expanded && <ChildList key="children" className={styles.children} initial={reduced ? false : { height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={reduced ? { opacity: 0, transition: { duration: 0 } } : { height: 0, opacity: 0 }} transition={spring}>
          {item.children!.map(child => row(child, depth + 1))}
        </ChildList>}
      </AnimatePresence>}
    </li>;
  };

  const nav = <motion.nav layoutScroll className={styles.nav} aria-label={t.nav}>
    {sections.map(section => <div key={section.id} className={styles.section}>
      {section.label && showLabels && <p className={styles.sectionLabel}>{section.label}</p>}
      <ul className={styles.list}>{section.items.map(item => row(item, 0))}</ul>
    </div>)}
  </motion.nav>;
  const person = user && <div className={styles.user}>
    <Avatar name={user.name} src={user.avatar} size="sm" />
    {showLabels && <span className={styles.userText}><span className={styles.userName}>{user.name}</span>{user.email && <span className={styles.userEmail}>{user.email}</span>}</span>}
  </div>;
  const body = typeof children === "function" ? children({ active: found?.item, compact }) : children;

  return <div ref={root} className={[styles.root, className].filter(Boolean).join(" ")} style={{ height, ...style }} data-collapsed={collapsed || undefined} data-narrow={narrow || undefined}>
    {!narrow && <motion.aside className={styles.sidebar} initial={false} animate={{ width: collapsed ? RAIL : FULL }} transition={spring}>
      <div className={styles.brand}>{showLabels ? brand : null}</div>
      {nav}
      {person}
    </motion.aside>}
    <div className={styles.main}>
      <header className={styles.header}>
        {narrow
          ? <button type="button" className={styles.iconButton} aria-label={t.menu} aria-expanded={sheet} aria-haspopup="dialog" onClick={() => setSheet(true)}><Menu size={16} aria-hidden="true" /></button>
          : <button type="button" className={styles.iconButton} aria-label={t.toggle} aria-expanded={!collapsed} aria-keyshortcuts="[" onClick={() => setCollapsed(!collapsed)}><PanelLeft size={16} aria-hidden="true" /></button>}
        <nav aria-label={t.breadcrumb} className={styles.crumbs}>
          <ol>
            {crumbs.map((crumb, index) => <li key={`${index}-${crumb}`} aria-current={index === crumbs.length - 1 ? "page" : undefined}>
              <AnimatePresence mode="popLayout" initial={false}>
                <motion.span key={crumb} className={styles.crumb} initial={reduced ? false : { opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={reduced ? { opacity: 0, transition: { duration: 0 } } : { opacity: 0, y: -6 }} transition={spring}>{crumb}</motion.span>
              </AnimatePresence>
            </li>)}
          </ol>
        </nav>
      </header>
      <div ref={content} className={styles.content} data-compact={compact || undefined}>{body}</div>
    </div>
    {narrow && <Drawer open={sheet} onOpenChange={setSheet}>
      <DrawerContent side="left" closeLabel="Fermer la navigation" title={typeof brand === "string" ? brand : t.nav} container={root.current} className={styles.sheet}
        onOpenAutoFocus={event => { event.preventDefault(); requestAnimationFrame(() => (rows.current.get(activeId ?? "") ?? rows.current.values().next().value)?.focus()); }}>
        <div id={`${uid}-sheet`} className={styles.sheetBody}>{nav}{person}</div>
      </DrawerContent>
    </Drawer>}
    <LiveStatus>{status}</LiveStatus>
  </div>;
}

export default SidebarRail;
